import { useState, useEffect, useRef, useContext } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useToast, useDisclosure } from "@chakra-ui/react";
import { useNavigate } from "react-router";
import { EDSHOP_IMAGE_RESOURCES, EDSHOP_AUDIO_RESOURCES, EDSHOP_PDF_RESOURCES, EDSHOP_PPT_RESOURCES, EDSHOP_VIDEOS } from "../../graphql/queries";
import { DELETE_RESOURCE, DELETE_VIMEO_VIDEO } from "../../graphql/mutations";
import { PublisherAndUserContext } from "../../contexts/PublisherAndUserContext";
import { useVideoUploader } from "../../hooks/useVideoUploader";
import { useBreadcrumbs } from '../../contexts/BreadcrumbProvider';

export const useResource = ({type}) => {
    const { user, publisher } = useContext(PublisherAndUserContext);
    const { uploadVideo } = useVideoUploader();
    const [loading, setLoading] = useState(false);
    const [hasMorePage, setHasMorePage] = useState(false);
    const [resources, setResources] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [filterType, setFilterType] = useState(type);
    const toast = useToast();
    const endOfListRef = useRef(null);
    const deleteResourceRef = useRef({});
    const navigate = useNavigate();
    const { addBreadcrumb } = useBreadcrumbs();

    const { isOpen: isUploadOpen, onOpen: onUploadOpen, onClose: onUploadClose } = useDisclosure();
    const { isOpen: isVideoUploadOpen, onOpen: onVideoUploadOpen, onClose: onVideoUploadClose } = useDisclosure();
    const {
        isOpen: isDeleteModalOpen,
        onOpen: onDeleteModelOpen,
        onClose: onDeleteModelClose,
    } = useDisclosure();
    const {
        isOpen: isTokenExhaustOpen,
        onOpen: onTokenExhaustOpen,
        onClose: onTokenExhaustClose,
      } = useDisclosure();
    const {
        isOpen: isTokenExhaustUpgradeToProOpen,
        onOpen: onTokenExhaustUpgradeToProOpen,
        onClose: onTokenExhaustUpgradeToProClose,
    } = useDisclosure();

    const [getImageResources, { data: imageData, error: imageError, fetchMore: fetchMoreImages }] = useLazyQuery(EDSHOP_IMAGE_RESOURCES);
    const [getAudioResources, { data: audioData, error: audioError, fetchMore: fetchMoreAudios }] = useLazyQuery(EDSHOP_AUDIO_RESOURCES);
    const [getPdfResources, { data: pdfData, error: pdfError, fetchMore: fetchMorePdfs }] = useLazyQuery(EDSHOP_PDF_RESOURCES);
    const [getPptResources, { data: pptData, error: pptError, fetchMore: fetchMorePpts }] = useLazyQuery(EDSHOP_PPT_RESOURCES);
    const [getVideos, { data: videoData, error: videoError, fetchMore: fetchMoreVideos }] = useLazyQuery(EDSHOP_VIDEOS);

    useEffect(() => {
        if (imageData) {
            setResources(imageData.edshopifyImageResources.resources || []);
            setHasMorePage(imageData.edshopifyImageResources.hasMore);
            setCurrentPage(imageData.edshopifyImageResources.hasMore ? currentPage + 1 : currentPage);
            setLoading(false);
        }
        if (imageError) {
            setLoading(false);
            toast({ position: "top-right", title: "Error fetching images.", status: "error", duration: 2000, isClosable: true });
            console.error(imageError);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageData, imageError]);

    useEffect(() => {
        if (audioData) {
            setResources(audioData.edshopifyAudioResources.resources || []);
            setHasMorePage(audioData.edshopifyAudioResources.hasMore);
            setCurrentPage(audioData.edshopifyAudioResources.hasMore ? currentPage + 1 : currentPage);
            setLoading(false);
        }
        if (audioError) {
            setLoading(false);
            toast({ position: "top-right", title: "Error fetching audios.", status: "error", duration: 2000, isClosable: true });
            console.error(audioError);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioData, audioError]);

    useEffect(() => {
        if (pdfData) {
            setResources(pdfData.edshopifyPdfResources.resources || []);
            setHasMorePage(pdfData.edshopifyPdfResources.hasMore);
            setCurrentPage(pdfData.edshopifyPdfResources.hasMore ? currentPage + 1 : currentPage);
            setLoading(false);
        }
        if (pdfError) {
            setLoading(false);
            toast({ position: "top-right", title: "Error fetching PDFs.", status: "error", duration: 2000, isClosable: true });
            console.error(pdfError);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfData, pdfError]);

    useEffect(() => {
        if (pptData) {
            setResources(pptData.edshopifyPptResources.resources || []);
            setHasMorePage(pptData.edshopifyPptResources.hasMore);
            setCurrentPage(pptData.edshopifyPptResources.hasMore ? currentPage + 1 : currentPage);
            setLoading(false);
        }
        if (pptError) {
            setLoading(false);
            toast({ position: "top-right", title: "Error fetching PPTs.", status: "error", duration: 2000, isClosable: true });
            console.error(pptError);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pptData, pptError]);

    useEffect(() => {
        if (videoData) {
            setResources(videoData.edshopifyVideos.videos || []);
            setHasMorePage(videoData.edshopifyVideos.hasMore);
            setCurrentPage(videoData.edshopifyVideos.hasMore ? currentPage + 1 : currentPage);
            setLoading(false);
        }
        if (videoError) {
            setLoading(false);
            toast({ position: "top-right", title: "Error fetching videos.", status: "error", duration: 2000, isClosable: true });
            console.error(videoError);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoData, videoError]);

    const [deleteResource, { loading: deleting }] = useMutation(DELETE_RESOURCE, {
        update(cache, { data: { edshopifyDeleteResource } }, { variables }) {
            if (!edshopifyDeleteResource) return;
            const { id, folderType, fileName } = variables?.where;
            const fieldMap = {
                images: 'edshopifyImageResources',
                audios: 'edshopifyAudioResources',
                pdfs: 'edshopifyPdfResources',
                ppts: 'edshopifyPptResources'
            };
            const cacheField = fieldMap[folderType];
            if (cacheField) {
                cache.modify({
                    fields: {
                        [cacheField](existing = {}, { readField }) {
                            const updated = existing.resources.filter(
                                (res) => readField('key', res) !== `${id}/${folderType}/${fileName}`
                            );
                            return { ...existing, resources: updated };
                        }
                    }
                });
            }
            onDeletePopupClose();
            toast({
                position: "top-right",
                title: `Deleted ${fileName}`,
                status: "success",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const [deleteViemoVideo, { loading: deletingVimeoVideo }] = useMutation(DELETE_VIMEO_VIDEO, {
        update(cache, { data: { edshopifyDeleteVimeoVideo } }, { variables }) {
            if (!edshopifyDeleteVimeoVideo?.success) return;

            const { videoId } = variables?.where;
            cache.modify({
                fields: {
                    edshopifyVideos(existingedshopifyVideosRefs = {}, { readField }) {
                        const updatedVideos = existingedshopifyVideosRefs.videos.filter(
                            (videoRef) => readField('id', videoRef) !== videoId
                        );
                        return {
                            ...existingedshopifyVideosRefs,
                            videos: updatedVideos,
                        };
                    },
                },
            });
            onDeletePopupClose();
            toast({
                position: "top-right",
                title: `Deleted Video`,
                status: "success",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const handleVideoUpload = (data) => {
        uploadVideo(data);
        onVideoUploadClose();
    }

    const onDeletePopupClose = () => {
        deleteResourceRef.current = {};
        onDeleteModelClose();
    }

    const handleDelete = async () => {
        if (filterType !== 'videos') {
            const [id, folderType, fileName] = deleteResourceRef?.current?.key?.split('/')
            deleteResource({
                variables: {
                    where: {
                        id,
                        folderType,
                        fileName
                    }
                }
            })
        }
        if (filterType === 'videos') {
            const {id} = deleteResourceRef?.current
            deleteViemoVideo({
                variables: {
                    where: {
                        id: publisher?.id,
                        videoId: id
                    }
                }
            })
        }
    };

    const buyCredits = () => {
        addBreadcrumb("Credits Info", "/credits-info", true)
        navigate("/credits-info")
    }

    const upgradeToPro = () => {
        addBreadcrumb("Pricing Plan", "/pricingplan", true)
        navigate("/pricingplan")
    }

    const updateFilterType = (e) => {
        const newType = e.target.value;
        setFilterType(newType);
        setCurrentPage(1);
        setLoading(true);
        setResources([]);
        const fetchMap = {
            images: getImageResources,
            audios: getAudioResources,
            pdfs: getPdfResources,
            ppts: getPptResources,
            videos: getVideos
        };
        const fetchQuery = fetchMap[newType];
        fetchQuery?.({ variables: { where: { id: publisher?.id, pageSize: 50, page: 1 } }});
    };

    useEffect(() => {      
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting || !hasMorePage) return;
                const fetchMap = {
                    images: fetchMoreImages,
                    audios: fetchMoreAudios,
                    pdfs: fetchMorePdfs,
                    ppts: fetchMorePpts,
                    videos: fetchMoreVideos
                };
                const fetchMoreFn = fetchMap[filterType];
                fetchMoreFn?.({ variables: { pageSize: 50, page: currentPage }});
            },
            { threshold: 1.0 } // Trigger when the element is fully visible
        );

        if (endOfListRef.current) {
            observer.observe(endOfListRef.current);
        }

        return () => {
            if (endOfListRef.current) {
                observer.unobserve(endOfListRef?.current);
            }
        };
    }, [fetchMoreImages, fetchMoreAudios, fetchMorePdfs, fetchMorePpts, hasMorePage, currentPage, filterType, fetchMoreVideos]);

    useEffect(() => {
        if (!publisher?.id) return;
        setLoading(true);

        const fetchMap = {
            images: getImageResources,
            audios: getAudioResources,
            pdfs: getPdfResources,
            ppts: getPptResources,
            videos: getVideos
        };
        const fetchQuery = fetchMap[type];
        fetchQuery?.({ variables: { where: { id: publisher?.id, pageSize: 50, page: 1 } }});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publisher?.id])

    return {
        loading,
        resources,
        filterType,
        deleteResourceRef,
        endOfListRef,
        deleting,
        deletingVimeoVideo,
        updateFilterType,
        isUploadOpen,
        onUploadOpen,
        onUploadClose,
        isVideoUploadOpen,
        onVideoUploadOpen,
        onVideoUploadClose,
        isDeleteModalOpen,
        onDeleteModelOpen,
        onDeleteModelClose,
        onDeletePopupClose,
        handleVideoUpload,
        handleDelete,
        setFilterType,
        user,
        publisher,
        isTokenExhaustOpen,
        onTokenExhaustOpen,
        onTokenExhaustClose,
        isTokenExhaustUpgradeToProOpen,
        onTokenExhaustUpgradeToProOpen,
        onTokenExhaustUpgradeToProClose,
        buyCredits,
        upgradeToPro,
    }
}